import { currentLocale } from '@/i18n/locale';
import { getEffectiveOutcome } from '@/utils/callUtils';

/**
 * Ce que l'ecran d'historique calcule avant d'afficher quoi que ce soit.
 *
 * Quatorze periodes, la resolution du statut, de la direction et de la date
 * d'un appel, et le nom d'un fichier d'enregistrement : tout cela vivait
 * dans CallHistoryScreen, 1420 lignes, sans un test. Les bornes de periode
 * sont la partie qui se trompe en silence, parce qu'une erreur d'un jour ne
 * se voit pas a l'oeil.
 *
 * `maintenant` est un parametre : sans lui, aucune de ces bornes n'etait
 * verifiable.
 */

export const PERIOD_VALUES = [
  'all', 'today', 'yesterday', 'this_week', 'last_week', 'this_month',
  'last_month', 'this_year', 'last_year', 'last_7_days', 'last_14_days',
  'last_30_days', 'last_365_days', 'custom',
];

// Status labels come exclusively from `outcomeLabel()` (callOutcomeLabels.ts,
// ADR-032 6ter rule 1): this table only keeps the view-specific visuals
// (badge color) per outcome value, never a competing label.
// #1 — « iOS Filter comme statut a part entiere » : present dans
// l'historique et dans ses libelles d'export (le CSV lit STATUS_MAP).
// 2026-05-14 — ajout des outcomes humain qui apparaissaient en raw
// string dans l'historique ("not_interested", "callback_requested").
export const STATUS_TONE = {
  meeting_booked:     'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  contacted:          'bg-blue-500/15 text-blue-400 border-blue-500/30',
  interested:         'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  not_interested:     'bg-rose-500/15 text-rose-400 border-rose-500/30',
  callback_requested: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  wrong_number:       'bg-stone-500/15 text-stone-400 border-stone-500/30',
  no_answer:          'bg-amber-500/15 text-amber-400 border-amber-500/30',
  voicemail:          'bg-violet-500/15 text-violet-400 border-violet-500/30',
  ios_filter:         'bg-orange-500/15 text-orange-400 border-orange-500/30',
  canceled:           'bg-slate-500/15 text-slate-400 border-slate-500/30',
  busy:               'bg-orange-500/15 text-orange-400 border-orange-500/30',
  failed:             'bg-red-500/15 text-red-400 border-red-500/30',
  not_reachable:      'bg-rose-500/15 text-rose-400 border-rose-500/30',
  left_voicemail:     'bg-purple-500/15 text-purple-400 border-purple-500/30',
};

export const STATUS_VALUES = Object.keys(STATUS_TONE);

export const MIN_DURATION_OPTIONS = [
  { value: '0',   label: '≥ 0s' },
  { value: '30',  label: '≥ 30s' },
  { value: '60',  label: '≥ 60s' },
  { value: '90',  label: '≥ 90s' },
  { value: '120', label: '≥ 120s' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns the start/end Date pair for a given period value.
 * Returns [null, null] when no date restriction applies.
 */
/** Ce que ces fonctions lisent d'un appel. */
export interface AppelLu {
  direction?: string | null;
  call_status?: string | null;
  call_outcome?: string | null;
  outcome?: string | null;
  created_date?: string | null;
  started_at?: string | null;
  connected_at?: string | null;
  updated_date?: string | null;
  duration_seconds?: number | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
}

export type Periode = string;

/** Les deux bornes d'une periode, ou null quand elle n'en a pas. */
export type Bornes = [Date | null, Date | null];

export function getPeriodRange(
  period: Periode,
  customStart?: string | null,
  customEnd?: string | null,
  maintenant: Date = new Date(),
): Bornes {
  const now = new Date(maintenant);

  const startOf = (unit: 'day' | 'week' | 'month' | 'year') => {
    const d = new Date(now);
    if (unit === 'day')   { d.setHours(0, 0, 0, 0); return d; }
    if (unit === 'week')  { d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1)); d.setHours(0, 0, 0, 0); return d; }
    if (unit === 'month') { d.setDate(1); d.setHours(0, 0, 0, 0); return d; }
    if (unit === 'year')  { d.setMonth(0, 1); d.setHours(0, 0, 0, 0); return d; }
    return d;
  };

  const daysAgo = (n: number) => { const d = new Date(now); d.setDate(d.getDate() - n); d.setHours(0, 0, 0, 0); return d; };

  switch (period) {
    // #22 -- aucune borne = aucun filtrage de date (cf. le garde
    // `if (rangeStart || rangeEnd)` cote filtrage).
    case 'all':          return [null, null];
    case 'today':        return [startOf('day'), now];
    case 'yesterday': {
      const s = daysAgo(1);
      const e = new Date(s); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case 'this_week':    return [startOf('week'), now];
    case 'last_week': {
      const s = startOf('week'); s.setDate(s.getDate() - 7);
      const e = new Date(s); e.setDate(e.getDate() + 6); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case 'this_month':   return [startOf('month'), now];
    case 'last_month': {
      const s = startOf('month'); s.setMonth(s.getMonth() - 1);
      const e = new Date(s); e.setMonth(e.getMonth() + 1); e.setDate(0); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case 'this_year':    return [startOf('year'), now];
    case 'last_year': {
      const s = startOf('year'); s.setFullYear(s.getFullYear() - 1);
      const e = new Date(s); e.setFullYear(e.getFullYear() + 1); e.setDate(0); e.setHours(23, 59, 59, 999);
      return [s, e];
    }
    case 'last_7_days':   return [daysAgo(7), now];
    case 'last_14_days':  return [daysAgo(14), now];
    case 'last_30_days':  return [daysAgo(30), now];
    case 'last_365_days': return [daysAgo(365), now];
    case 'custom': {
      const s = customStart ? new Date(customStart + 'T00:00:00') : null;
      const e = customEnd   ? new Date(customEnd   + 'T23:59:59') : null;
      return [s, e];
    }
    default: return [null, null];
  }
}

/** Le resultat d'un appel, avec la correction de duree partagee. */
export function resolveCallStatus(call: AppelLu): string {
  return getEffectiveOutcome(call);
}

/** Resolve a call's direction — inbound / outbound */
export function resolveDirection(call: AppelLu): 'inbound' | 'outbound' {
  if (call.direction === 'inbound' || call.direction === 'outbound') return call.direction;
  // Inbound calls typically have no started_at or have an inbound flag
  if (call.call_status === 'inbound') return 'inbound';
  return 'outbound';
}

/** Resolve the timestamp to sort/filter on */
export function resolveDate(call: AppelLu): string | undefined {
  return call.created_date || call.started_at || call.connected_at || call.updated_date || undefined;
}

export function buildRecordingFilename(call: AppelLu, t: (cle: string) => string): string {
  // #32 -- "Nom contact - Nom entreprise - Date". C'etait le POSTE en
  // troisieme position ; la date est ce qui distingue deux enregistrements
  // du meme contact, pas son titre.
  const brut = call.created_date || call.started_at || call.connected_at;
  const d = brut ? new Date(brut) : null;
  const date = d && !isNaN(d.getTime())
    ? d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')
    : null;
  const parts = [call.prospect_name, call.prospect_company, date].filter(Boolean);
  return parts.length > 0 ? parts.join(' - ').replace(/[/\\?%*:|"<>]/g, '_') + '.mp3' : t('dialer:callHistory.detail.recording') + '.mp3';
}


/**
 * The history's « Nb appels » column and the export, indexed once per phone
 * (they scanned every call and prospect per row). Calls without a phone are
 * counted together, as before.
 */
export function callsPerPhone(calls: ReadonlyArray<{ prospect_phone?: string | null }>): Map<string | null | undefined, number> {
  const m = new Map<string | null | undefined, number>();
  for (const c of calls) m.set(c.prospect_phone, (m.get(c.prospect_phone) || 0) + 1);
  return m;
}

/** The LinkedIn of the first prospect with that phone that has one. */
export function linkedinPerPhone(prospects: ReadonlyArray<{ prospect_phone?: string | null; linkedin_url?: string | null }>): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of prospects) if (p.prospect_phone && p.linkedin_url && !m.has(p.prospect_phone)) m.set(p.prospect_phone, p.linkedin_url);
  return m;
}
